import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideZonelessChangeDetection,
} from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import {
  provideRouter,
  TitleStrategy,
  withNavigationErrorHandler,
  withPreloading,
} from '@angular/router';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  TimeagoCustomFormatter,
  TimeagoFormatter,
  TimeagoIntl,
  TimeagoModule,
} from 'ngx-timeago';

import dayjs from 'dayjs/esm';
import 'dayjs/esm/locale/de.js';
import 'dayjs/esm/locale/es.js';
import 'dayjs/esm/locale/fr.js';
import 'dayjs/esm/locale/it.js';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import weekday from 'dayjs/esm/plugin/weekday';
import week from 'dayjs/esm/plugin/weekOfYear';

import { DEFAULT_LANGUAGE } from '@constants';
import { IdlePreloadingStrategy, PageTitleStrategy } from '@services';
import {
  clearSessionOnLogout,
  provideBrowserStorageCleanup,
  provideSessionSync,
  reducer,
} from '@store';
import { routes } from './app.routes';
import { STRHTTPInterceptor } from './core/interceptors/str.interceptor';
import { redirectOutageNavigationErrors } from './core/router/navigation-error-handler';

dayjs.extend(week);
dayjs.extend(customParseFormat);
dayjs.extend(weekday);

export const appConfig: ApplicationConfig = {
  providers: [
    Title,
    provideRouter(
      routes,
      withNavigationErrorHandler(redirectOutageNavigationErrors),
      withPreloading(IdlePreloadingStrategy)
    ),
    { provide: TitleStrategy, useExisting: PageTitleStrategy },
    provideZonelessChangeDetection(),
    provideHttpClient(withXhr(), withInterceptors([STRHTTPInterceptor])),
    provideStore(reducer, { metaReducers: [clearSessionOnLogout] }),
    provideBrowserStorageCleanup(),
    provideSessionSync(),
    ...(typeof ngDevMode === 'undefined' || ngDevMode
      ? [provideStoreDevtools({ maxAge: 25, trace: true })]
      : []),

    provideTranslateService({
      loader: provideTranslateHttpLoader(),
      fallbackLang: DEFAULT_LANGUAGE,
    }),
    importProvidersFrom(
      BrowserModule,
      TimeagoModule.forRoot({
        intl: TimeagoIntl,
        formatter: {
          provide: TimeagoFormatter,
          useClass: TimeagoCustomFormatter,
        },
      })
    ),
  ],
};
